import * as React from 'react';

import { useNavigate } from 'js/lib/useRouter';

import { useSystemProgram } from 'bundles/enterprise-admin-hooks/hooks/useSystemProgram';
import InviteErrorDialog from 'bundles/enterprise-admin-learners/components/invite-learners/InviteErrorDialog';
import InviteSuccessDialog from 'bundles/enterprise-admin-learners/components/invite-learners/InviteSuccessDialog';
import NoInvitesSentDialog from 'bundles/enterprise-admin-learners/components/invite-learners/NoInvitesSentDialog';
import PartialInviteSuccessDialog from 'bundles/enterprise-admin-learners/components/invite-learners/PartialInviteSuccessDialog';
import type { InviteLearnersResult } from 'bundles/enterprise-admin-learners/hooks/useInviteLearners';
import { useManageableGroups } from 'bundles/enterprise-admin-learners/hooks/useManageableGroups';
import { useThirdPartyOrganization } from 'bundles/enterprise-admin/ThirdPartyOrganizationProvider';

import _t from 'i18n!nls/enterprise-admin-learners';

export type InviteOutcomeSummaryProps = {
  open: boolean;
  /** Result of an invite submission — the literal `'error'` covers a total-failure/thrown case. */
  outcome: InviteLearnersResult | 'error';
  onClose: () => void;
};

/**
 * Success/partial-success/error summary shown after an email invite submission
 * (ENTADMIN-1957). Accepts `InviteLearnersResult | 'error'` directly, the same union
 * `EmailInviteModal` already produces — no CSV-specific branching, since ENTADMIN-2082's
 * CSV submission will have its own, differently-shaped result to adapt into this component.
 *
 * A thin dispatcher: resolves the shared group/navigation context once, then picks which
 * of the four outcome dialogs to render. `InviteSuccessDialog` and `PartialInviteSuccessDialog`
 * match design mockups; `InviteErrorDialog` and `NoInvitesSentDialog` have no mockup yet and
 * keep their earlier, simpler treatment.
 */
const InviteOutcomeSummary = ({ open, outcome, onClose }: InviteOutcomeSummaryProps) => {
  const navigate = useNavigate();
  const org = useThirdPartyOrganization();
  const manageableGroups = useManageableGroups();
  const { systemProgram } = useSystemProgram(org.id);

  if (outcome === 'error') {
    return <InviteErrorDialog open={open} onClose={onClose} />;
  }

  const { failed, successful, membershipEmails, programId } = outcome;
  const invitedCount = successful.length + membershipEmails.length;
  const hasInvited = invitedCount > 0;
  const hasFailures = failed.length > 0;
  // The system program can also appear in `manageableGroups` — always treat a match
  // against it as the org-level (no group known) invite flow, not a real group.
  const isSystemProgram = Boolean(systemProgram) && programId === systemProgram?.id;
  const group = manageableGroups.find((manageableGroup) => manageableGroup.id === programId);

  if (!hasInvited) {
    return <NoInvitesSentDialog open={open} onClose={onClose} failed={failed} />;
  }

  const groupName = isSystemProgram ? undefined : group?.name;
  const canViewGroup = !isSystemProgram && Boolean(group?.slug);
  const onViewGroup = () => {
    if (canViewGroup && group?.slug) navigate(`/o/${org.slug}/admin/groups/${group.slug}`);
    onClose();
  };
  const onViewAllLearners = () => {
    navigate(`/o/${org.slug}/admin/learners`);
    onClose();
  };

  if (!hasFailures) {
    return (
      <InviteSuccessDialog
        open={open}
        onClose={onClose}
        invitedCount={invitedCount}
        groupName={groupName}
        canViewGroup={canViewGroup}
        onViewGroup={onViewGroup}
        onViewAllLearners={onViewAllLearners}
      />
    );
  }

  return (
    <PartialInviteSuccessDialog
      open={open}
      onClose={onClose}
      invitedCount={invitedCount}
      failed={failed}
      groupName={groupName ?? _t('the group')}
      canViewGroup={canViewGroup}
      onViewGroup={onViewGroup}
    />
  );
};

export default InviteOutcomeSummary;
